import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, of, Subject, switchMap, tap } from 'rxjs';
import { FilterOperators } from 'src/app/common/enums';
import { DictionaryService } from 'src/app/data/dictionary.service';
import { UserService } from 'src/app/data/user.service';
import { FilterType } from 'src/app/list-filter/enums/filter-type.enum';
import { FilterService } from 'src/app/list-filter/services/filter.service';
import { DictionaryItem } from 'src/app/models/DictionaryItem';
import { FilterTypeEnum } from 'src/app/models/enums/filter-type-enum';

@Injectable()
export class WorkerAgreementListFilterService extends FilterService {
  protected userService: UserService;
  private translateService = inject(TranslateService);
  private dictionaryService = inject(DictionaryService);

  protected filterType = FilterTypeEnum.WorkerAgreementsList;
  private subject: Subject<string> = new Subject<string>();
  private mpks$ = new BehaviorSubject<DictionaryItem[]>(null);


  constructor() {
    const userService = inject(UserService);

    super(FilterTypeEnum.WorkerAgreementsList, userService);
    this.userService = userService;


    this.config = [
      {
        id: 1,
        name: 'employerId',
        label: this.translateService.instant('Employer'),
        property: 'EmployerId',
        operator: FilterOperators.Equal,
        default: true,
        type: FilterType.Autocomplete,
        datasource$: this.subject.asObservable().pipe(
          switchMap((value: string) => (value ? this.dictionaryService.getEmployers(value) : of([]))),
        ),
        searchTextChangeCallback: (value: any) => this.subject.next(value),
      },
      {
        id: 2,
        name: 'employerObjectId',
        label: this.translateService.instant('EmployerObject'),
        property: 'EmployerObjectId',
        operator: FilterOperators.Equal,
        default: true,
        type: FilterType.SearchableMultiselect,
        getData: (value: string, page: number, pageSize: number) => this.dictionaryService.getEmployerObjectsPaged(value, page, pageSize),
        changeCallback: (value: any[]) => this.mpks$.next(value?.map(item => ({ Id: item.Id, Name: item.Mpk })) ?? null)
      },
      {
        id: 3,
        name: 'agreementTypeId',
        label: this.translateService.instant('AgreementType'),
        property: 'AgreementTypeId',
        operator: FilterOperators.Equal,
        default: true,
        type: FilterType.Select,
        datasource$: this.dictionaryService.getAgreementTypes(),
        searchTextChangeCallback: (value: any) => this.subject.next(value),
      },
      {
        id: 4,
        name: 'lastName',
        label: this.translateService.instant('LastName'),
        property: 'LastName',
        default: true,
        operator: FilterOperators.Contains,
        type: FilterType.Text
      },
      {
        id: 5,
        name: 'firstName',
        label: this.translateService.instant('FirstName'),
        property: 'FirstName',
        default: true,
        operator: FilterOperators.Contains,
        type: FilterType.Text
      },
      {
        id: 6,
        name: 'pesel',
        label: this.translateService.instant('Pesel'),
        property: 'Pesel',
        default: true,
        operator: FilterOperators.StringEqual,
        type: FilterType.Text
      },
      {
        id: 7,
        name: 'companyId',
        label: this.translateService.instant('Company'),
        property: 'CompanyId',
        operator: FilterOperators.Equal,
        type: FilterType.Autocomplete,
        datasource$: this.subject.asObservable().pipe(
          switchMap((value: string) => (value ? this.dictionaryService.getCompanies(value) : of([]))),
        ),
        searchTextChangeCallback: (value: any) => this.subject.next(value),
      },
      {
        id: 8,
        name: 'locationId',
        label: this.translateService.instant('Location'),
        property: 'LocationId',
        operator: FilterOperators.Equal,
        type: FilterType.Autocomplete,
        datasource$: this.subject.asObservable().pipe(
          switchMap((value: string) => (value ? this.dictionaryService.getLocations(value) : of([]))),
        ),
        searchTextChangeCallback: (value: any) => this.subject.next(value),
      },
      {
        id: 9,
        name: 'employmentDateFrom',
        label: this.translateService.instant('EmploymentDateFrom'),
        property: 'AgreementStartDate',
        operator: FilterOperators.DateGreaterThanOrEqual,
        type: FilterType.Date
      },
      {
        id: 10,
        name: 'employmentDateTo',
        label: this.translateService.instant('WL-AgreementEndDate'),
        property: 'AgreementEndEffectiveDate',
        operator: FilterOperators.DateLessThanOrEqual,
        type: FilterType.Date
      },
      {
        id: 11,
        name: 'workerStatusId',
        label: this.translateService.instant('WL-WorkerStatus'),
        property: 'WorkerStatusId',
        operator: FilterOperators.Equal,
        type: FilterType.Select,
        datasource$: this.dictionaryService.getWorkerStatuses(),
      },
      {
        id: 12,
        name: 'agreementStatusId',
        label: this.translateService.instant('WL-AgreementStatus'),
        property: 'AgreementStatusId',
        operator: FilterOperators.Equal,
        type: FilterType.Select,
        datasource$: this.dictionaryService.getAgreementStatuses(),
      },

      {
        id: 13,
        name: 'employmentType',
        label: this.translateService.instant('EmploymentType'),
        property: 'EmploymentTypeId',
        operator: FilterOperators.Equal,
        type: FilterType.Select,
        datasource$: this.dictionaryService.getEmploymentTypes(),
      },
      {
        id: 14,
        name: 'document',
        label: this.translateService.instant('Passport'),
        property: 'Passport',
        operator: FilterOperators.Contains,
        type: FilterType.Text
      },
      {
        id: 15,
        name: 'legalizationStatusId',
        label: this.translateService.instant('WL-LegalizationStatus'),
        property: 'LegalizationStatusId',
        operator: FilterOperators.Custom,
        type: FilterType.Select,
        datasource$: this.dictionaryService.getLegalizationStatuses(true),
      },
      {
        id: 16,
        name: 'employmentConditionsConfirmationStatusId',
        label: this.translateService.instant('WL-EmploymentConditionsConfirmationStatus'),
        property: 'EmploymentConditionsConfirmationStatusId',
        operator: FilterOperators.Equal,
        type: FilterType.Select,
        datasource$: this.dictionaryService.getEmploymentConditionsConfirmationStatusesForWorkerAgreements(),
      },
      {
        id: 17,
        name: 'mpk',
        label: this.translateService.instant('MPK'),
        type: FilterType.ReadonlyInlineListFilter,
        property: 'mpk',
        datasource$: this.mpks$
      },
    ]
  }
}
